<template>
  <div class="kanban-wrap">
    <SlickList
      class="kanban-board"
      tag="div"
      axis="x"
      :list="boards"
      :useDragHandle="true"
      :transitionDuration="150"
      appendTo="#app"
      @update:list="moveBoard"
    >
      <SlickItem
        v-for="(board, index) in boards"
        :key="board.id"
        :index="index"
        tag="div"
        class="kanban-col"
      >
        <!-- Column header -->
        <div class="kanban-col__header">
          <DragHandle tag="div" class="kanban-col__drag-handle noselect">
            <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12">
              <circle cx="5" cy="3" r="1.3"/><circle cx="11" cy="3" r="1.3"/>
              <circle cx="5" cy="8" r="1.3"/><circle cx="11" cy="8" r="1.3"/>
              <circle cx="5" cy="13" r="1.3"/><circle cx="11" cy="13" r="1.3"/>
            </svg>
          </DragHandle>
          <span class="kanban-col__title">{{ board.title }}</span>
          <div class="kanban-col__header-actions">
            <IconBtn @click="startCreateNewTask(board)" title="Add task">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M8 3v10M3 8h10"/></svg>
            </IconBtn>
            <IconBtn @click="startEditBoard(board)" title="Board settings">
              <svg viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="3" r="1.2"/><circle cx="8" cy="8" r="1.2"/><circle cx="8" cy="13" r="1.2"/></svg>
            </IconBtn>
          </div>
        </div>

        <!-- Task list -->
        <SlickList
          class="kanban-col__list"
          :pressDelay="0"
          axis="y"
          :list="board.tasks"
          appendTo="#app"
          group="kanban"
          @sort-end="dragging = false"
          :transitionDuration="150"
          @update:list="moveTask(board.id, $event)"
        >
          <SlickItem
            v-for="(task, index) in board.tasks"
            :key="task.id"
            :index="index"
            @mousedown="dragging = true"
            class="kanban-col__item"
          >
            <TaskEntry
              :value="task"
              @edit="taskEditor(task)"
              @complete="confirmCompletionDialog(task)"
              @resetComplete="resetCompletionDialog(task)"
              @archive="confirmArchiveDialog(task)"
              @delete="confirmDeleteDialog(task)"
            />
          </SlickItem>
        </SlickList>

        <!-- Add task footer -->
        <button class="kanban-col__add" @click="startCreateNewTask(board)">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <path d="M8 3v10M3 8h10"/>
          </svg>
          Add task
        </button>
      </SlickItem>
    </SlickList>

    <!-- Task edit modal (with comments) -->
    <TaskEditModal
      :show="taskEditDialog.show"
      :task="taskEditDialog.selectedTask"
      :boards="boards"
      :loading="taskEditDialog.loading"
      @close="taskEditDialog.show = false"
      @save="onTaskEditSave"
    />

    <!-- Generic dialogs (create task, yesno confirmations, board edit) -->
    <Dialog
      v-for="(dialog, key) of dialogs"
      :key="key"
      :value="dialog.dialog"
      @close="() => { dialog.dialog = false; dialog.hasChanges = false }"
      :persistent="dialog.loading || (dialog.changeGuard && dialog.hasChanges)"
    >
      <DataForm
        :type="dialog.type"
        :value="dialog.form"
        :loading="dialog.loading"
        :disabled="dialog.loading"
        :allowCancel="true"
        @cancel="dialog.dialog = false"
        @update="dialogOk(key, $event)"
        :title="dialog.title"
        @updateHasChanges="dialog.hasChanges = $event"
      />
    </Dialog>
  </div>
</template>

<script>
  import { SlickList, SlickItem, DragHandle } from 'vue-slicksort'
  import DataForm from '../common/DataForm.vue'
  import Dialog from '../common/Dialog.vue'
  import TaskEntry from './TaskEntry.vue'
  import TaskEditModal from './TaskEditModal.vue'
  import IconBtn from '../common/IconBtn.vue'
  import moment from 'moment'
  import { getFirstLineFromHtml } from '../../utils'

  export default {
    components: { DataForm, Dialog, SlickList, SlickItem, DragHandle, TaskEntry, TaskEditModal, IconBtn },
    name: 'Tasks',
    props: ['project'],
    data: () => ({
      dragging: false,
      taskEditDialog: {
        show: false,
        loading: false,
        selectedTask: null
      },
      dialogs: {
        createTaskEditor: {
          dialog: false,
          loading: false,
          title: 'Create task',
          type: 'create',
          form: {
            description: {
              type: 'textarea',
              label: 'Description',
              default: '',
              validate: (val) => (val && getFirstLineFromHtml(val).length > 0) || 'Must be at least 1 character long'
            },
            plannedDurationHours: {
              type: 'number',
              label: 'Planned duration in hours',
              default: ''
            },
            deadline: {
              type: 'datetime',
              label: 'Deadline',
              default: ''
            },
            boardId: {
              type: 'select',
              label: 'Board',
              options: [],
              default: '',
              validate: (val) => !!val || 'A board must be selected'
            }
          }
        },
        confirmCompletionDialog: {
          dialog: false,
          loading: false,
          title: "Mark task as complete?",
          type: 'yesno',
          selectedTask: null,
          form: {}
        },
        resetCompletionDialog: {
          dialog: false,
          loading: false,
          title: 'Mark task as incomplete?',
          type: 'yesno',
          selectedTask: null,
          form: {}
        },
        confirmArchiveDialog: {
          dialog: false,
          loading: false,
          title: 'Archive this task?',
          type: 'yesno',
          selectedTask: null,
          form: {}
        },
        confirmDeleteDialog: {
          dialog: false,
          loading: false,
          title: 'Delete this task permanently?',
          type: 'yesno',
          selectedTask: null,
          form: {}
        },
        editBoardEditor: {
          dialog: false,
          loading: false,
          title: 'Edit board',
          type: 'edit',
          selectedBoard: null,
          form: {
            title: {
              type: 'text',
              label: 'Title',
              default: '',
              validate: (val) => (val && val.length > 0) || 'Must be at least 1 character long'
            }
          }
        }
      }
    }),
    mounted() {
      this.setDialogsOptions()
    },
    watch: {
      boards() { this.setDialogsOptions() },
      project() { this.setDialogsOptions() }
    },
    computed: {
      boards() {
        if (!this.project) return []
        return this.$store.getters.boardsOrderedWithTasks(this.project.id)
      }
    },
    methods: {
      setDialogsOptions() {
        const boardItems = this.boards.map(b => ({ value: b.id, text: b.title }))
        this.dialogs.createTaskEditor.form.boardId.options = boardItems
      },
      async dialogOk(formKey, payload) {
        this.dialogs[formKey].loading = true
        if (formKey === 'resetCompletionDialog') {
          await this.$store.dispatch('updateTask', {
            id: this.dialogs.resetCompletionDialog.selectedTask.id,
            completedAt: null
          })
        } else if (formKey === 'confirmCompletionDialog') {
          await this.$store.dispatch('updateTask', {
            id: this.dialogs.confirmCompletionDialog.selectedTask.id,
            completedAt: moment().format()
          })
        } else if (formKey === 'confirmArchiveDialog') {
          await this.$store.dispatch('updateTask', {
            id: this.dialogs.confirmArchiveDialog.selectedTask.id,
            archivedAt: moment().format()
          })
        } else if (formKey === 'confirmDeleteDialog') {
          await this.$store.dispatch('deleteTask', this.dialogs.confirmDeleteDialog.selectedTask.id)
        } else if (formKey === 'createTaskEditor') {
          if (!payload.plannedDurationHours) payload.plannedDurationHours = 0
          payload.projectId = this.project.id
          payload.deadline = payload.deadline || null
          await this.$store.dispatch('createTask', payload)
        } else if (formKey === 'editBoardEditor') {
          await this.$store.dispatch('updateBoard', {
            id: this.dialogs.editBoardEditor.selectedBoard.id,
            ...payload
          })
        }
        this.dialogs[formKey].hasChanges = false
        this.dialogs[formKey].dialog = false
        this.dialogs[formKey].loading = false
      },
      moveBoard(boards) {
        this.$store.dispatch('reorderBoards', { id: this.project.id, boards })
      },
      moveTask(boardId, tasks) {
        this.$store.dispatch('reorderTasks', { id: boardId, tasks, projectId: this.project.id })
      },
      taskEditor(task) {
        this.taskEditDialog.selectedTask = task
        this.taskEditDialog.show = true
      },
      async onTaskEditSave(payload) {
        this.taskEditDialog.loading = true
        await this.$store.dispatch('updateTask', {
          id: this.taskEditDialog.selectedTask.id,
          boardId: payload.boardId || this.taskEditDialog.selectedTask.boardId,
          description: payload.description,
          plannedDurationHours: payload.plannedDurationHours || 0,
          deadline: payload.deadline || null
        })
        this.taskEditDialog.loading = false
        this.taskEditDialog.show = false
      },
      confirmCompletionDialog(task) {
        this.dialogs.confirmCompletionDialog.selectedTask = task
        this.dialogs.confirmCompletionDialog.dialog = true
      },
      resetCompletionDialog(task) {
        this.dialogs.resetCompletionDialog.selectedTask = task
        this.dialogs.resetCompletionDialog.dialog = true
      },
      confirmArchiveDialog(task) {
        this.dialogs.confirmArchiveDialog.selectedTask = task
        this.dialogs.confirmArchiveDialog.dialog = true
      },
      confirmDeleteDialog(task) {
        this.dialogs.confirmDeleteDialog.selectedTask = task
        this.dialogs.confirmDeleteDialog.dialog = true
      },
      startCreateNewTask(board) {
        this.dialogs.createTaskEditor.form.boardId.default = board.id
        this.dialogs.createTaskEditor.dialog = true
      },
      startEditBoard(board) {
        this.dialogs.editBoardEditor.form.title.default = board.title
        this.dialogs.editBoardEditor.selectedBoard = board
        this.dialogs.editBoardEditor.dialog = true
      }
    }
  }
</script>

<style scoped>
.kanban-wrap {
  overflow-x: auto;
  padding: 0 0px 24px;
  height: 100%;
}

.kanban-board {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  min-height: 200px;
}

/* Column */
.kanban-col {
  flex: 0 0 290px;
  width: 290px;
  display: flex;
  flex-direction: column;
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 14px;
  overflow: hidden;
  box-sizing: border-box;
}

.kanban-col__header {
  display: flex;
  align-items: center;
  padding: 12px 14px 10px;
  gap: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.kanban-col__drag-handle {
  display: flex;
  align-items: center;
  cursor: grab;
  color: rgba(0, 0, 0, 0.2);
  padding: 2px 4px 2px 0;
  transition: color 0.12s;
  flex-shrink: 0;
}

.kanban-col__drag-handle:hover {
  color: rgba(0, 0, 0, 0.45);
}

.kanban-col__drag-handle:active {
  cursor: grabbing;
}

.kanban-col__title {
  flex: 1;
  font-size: 0.8rem;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.55);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.kanban-col__header-actions {
  display: flex;
  gap: 2px;
}

.kanban-col__list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 10px 4px;
  min-height: 40px;
}

.kanban-col__item {
  list-style: none;
  width: 270px;
  box-sizing: border-box;
}

.kanban-col__add {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.35);
  font-weight: 500;
  transition: color 0.12s, background 0.12s;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.kanban-col__add svg {
  width: 13px;
  height: 13px;
}

.kanban-col__add:hover {
  color: rgba(0, 0, 0, 0.6);
  background: rgba(0, 0, 0, 0.03);
}
</style>
